/* eslint-disable relay/must-colocate-fragment-spreads */
/* eslint-disable relay/unused-fields */
import React, { useCallback, useEffect, useMemo } from 'react'
import type { FC } from 'react'
import { createContext } from 'use-context-selector'
import { graphql } from 'babel-plugin-relay/macro'
import { commitMutation, useFragment } from 'react-relay-offline'

import { List } from '../../shared/types'
import type {
  InServiceListItem,
  Occurrence,
  OutOfStoreItem,
  WaitingListItem,
  OccurrencesTrees,
} from '../../shared/types'
import type {
  TurnListProvider_salesAssociatesConnection$data,
  TurnListProvider_salesAssociatesConnection$key,
} from './__generated__/TurnListProvider_salesAssociatesConnection.graphql'
import type { TurnListProviderMutation } from './__generated__/TurnListProviderMutation.graphql'
import {
  getListsInitialState,
  getPositionReferenceTimeByPositioningRule,
  getUnfinishedMovementsBySelectedMovement,
  buildOccurrencesTrees,
  getDisplaydServiceInfo,
  handleEventSendError,
  getServiceResultsAndRelations,
  createNewOutOfStoreItems,
  getLastPositioningRule,
} from './utils'
import { environment } from '../../../../core/components/RelayEnvironmentProvider'
import type {
  TurnListProvider_movement$data,
  TurnListProvider_movement$key,
} from './__generated__/TurnListProvider_movement.graphql'
import type { utils_salesAssociate$data } from './__generated__/utils_salesAssociate.graphql'
import { usePositionsLists } from '../../states/positionsLists'
import type { TurnListProvider_salesAssociateTrackingFlow$key } from './__generated__/TurnListProvider_salesAssociateTrackingFlow.graphql'
import type { TurnListProvider_sendServiceEvent_Mutation } from './__generated__/TurnListProvider_sendServiceEvent_Mutation.graphql'
import {
  findPositionListsAndSalesAssociatesDivergences,
  findSalesAssociatesOutOfStore,
} from './helpers'
import { logger } from '../../../../logger'
import { useSalesAssociate } from '../../../../core/globalState'

export type ListsItem = {
  waiting: WaitingListItem<
    TurnListProvider_movement$data,
    utils_salesAssociate$data
  >
  inService: InServiceListItem<
    TurnListProvider_movement$data,
    utils_salesAssociate$data
  >
  outOfStore: OutOfStoreItem<
    TurnListProvider_movement$data,
    utils_salesAssociate$data
  >
}

export type PositionsListItem = ListsItem[keyof ListsItem]

export type Lists = {
  waitingList: Array<ListsItem['waiting']>
  inServiceList: Array<ListsItem['inService']>
  outOfStoreList: Array<ListsItem['outOfStore']>
}

const salesAssociatesConnectionFragment = graphql`
  fragment TurnListProvider_salesAssociatesConnection on SalesAssociateConnection {
    edges {
      node {
        id
        ...utils_salesAssociate
      }
    }
    ...SalesAssociatesModal_salesAssociatesConnection
  }
`

const movementFragment = graphql`
  fragment TurnListProvider_movement on SalesAssociateTrackingMovementStep {
    id
    name
    positioningRule
    ...utils_unfinishedMovement
    ...OutOfStoreListItem_moviment
    ...MovementsModal_movementStep
    stepToClose {
      id
    }
    nextSteps {
      id
      positioningRule
      ...utils_unfinishedMovement
      ...OutOfStoreListItem_moviment
      ...MovementsModal_movementStep
      nextSteps {
        id
        positioningRule
        ...utils_unfinishedMovement
        ...OutOfStoreListItem_moviment
        ...MovementsModal_movementStep
        stepToClose {
          id
        }
      }
      stepToClose {
        id
        positioningRule
        ...OutOfStoreListItem_moviment
        ...MovementsModal_movementStep
      }
    }
  }
`

const salesAssociateTrackingFlowFragment = graphql`
  fragment TurnListProvider_salesAssociateTrackingFlow on SalesAssociateTrackingFlow {
    ...utils_buildOccurrencesTrees
  }
`

type ContextType = {
  movementFlow: TurnListProvider_movement$data
  occurrenceTrees: OccurrencesTrees
  moveWaitingListItemToInServiceList: (
    waitingListItem: ListsItem['waiting'],
    selectedOccurrences?: Occurrence[]
  ) => void
  moveInServiceListItemToWaitingList: (
    inServiceListItem: ListsItem['inService'],
    selectedOccurrences?: Occurrence[]
  ) => void
  moveOutOfStoreItemToWaitingList: (
    outOfSellerItem: ListsItem['outOfStore']
  ) => void
  moveWaitingListItemToOutOfStore: (
    waitingListItem: ListsItem['waiting'],
    selectedMovement: TurnListProvider_movement$data
  ) => void
  salesAssociates: TurnListProvider_salesAssociatesConnection$data
}

export const TurnListContext = createContext({} as ContextType)

type TurnListProviderProps = {
  salesAssociatesConnectionRef: TurnListProvider_salesAssociatesConnection$key
  movementRef: TurnListProvider_movement$key
  salesAssociateTrackingServiceFlowRef: TurnListProvider_salesAssociateTrackingFlow$key
}

export const TurnListProvider: FC<TurnListProviderProps> = ({
  salesAssociatesConnectionRef,
  movementRef,
  salesAssociateTrackingServiceFlowRef,
  children,
}) => {
  const {
    isPositionsListsEmpty,
    setPositionsLists,
    positionsLists,
    addItemsToOutOfStoreList,
    removeItemsFromLists,
    moveItemFromOutOfStoreToWaiting,
    moveItemFromWaitingListToInService,
    moveItemFromInServiceListToWaitingList,
    moveItemFromWaitingListToOutOfStore,
  } = usePositionsLists()

  const salesAssociates = useFragment<TurnListProvider_salesAssociatesConnection$key>(
    salesAssociatesConnectionFragment,
    salesAssociatesConnectionRef
  )

  useEffect(
    function updatePositionsListsSalesAssociates() {
      if (isPositionsListsEmpty && salesAssociates.edges?.length) {
        setPositionsLists(getListsInitialState(salesAssociates))

        return
      }

      const {
        salesAssociatesToAddIds,
        salesAssociatesToRemoveIds,
      } = findPositionListsAndSalesAssociatesDivergences(
        positionsLists,
        salesAssociates.edges.map((edge) => edge.node.id)
      )

      const newOutOfStoreItems = createNewOutOfStoreItems(
        salesAssociates
      ).filter((node) =>
        salesAssociatesToAddIds.includes(node.salesAssociate.id)
      )

      if (newOutOfStoreItems.length) {
        addItemsToOutOfStoreList(newOutOfStoreItems)
      }

      const outOfStoreSalesAssociatesIds = findSalesAssociatesOutOfStore(
        positionsLists,
        salesAssociatesToRemoveIds
      )

      if (outOfStoreSalesAssociatesIds.length) {
        removeItemsFromLists({
          salesAssociatesIds: outOfStoreSalesAssociatesIds,
        })
      }
    },
    [
      addItemsToOutOfStoreList,
      isPositionsListsEmpty,
      positionsLists,
      salesAssociates,
      setPositionsLists,
      removeItemsFromLists,
    ]
  )

  const movementFlow = useFragment<TurnListProvider_movement$key>(
    movementFragment,
    movementRef
  )

  const salesAssociateTrackingFlowData = useFragment<TurnListProvider_salesAssociateTrackingFlow$key>(
    salesAssociateTrackingFlowFragment,
    salesAssociateTrackingServiceFlowRef
  )

  const occurrenceTrees = useMemo(
    () => buildOccurrencesTrees(salesAssociateTrackingFlowData),
    [salesAssociateTrackingFlowData]
  )

  const { salesAssociate: currentSalesAssociate } = useSalesAssociate()

  const sendMovementEvent = useCallback(
    ({
      salesAssociateId = '',
      movementId = '',
    }: {
      salesAssociateId?: string
      movementId?: string
    }) => {
      commitMutation<TurnListProviderMutation>(environment, {
        mutation: graphql`
          mutation TurnListProviderMutation($input: CreateMovementInput!)
          @raw_response_type {
            createSalesAssociateTrackingMovement(input: $input) {
              success
            }
          }
        `,
        variables: {
          input: {
            movementId,
            salesAssociateId,
            date: new Date(),
            storeMasterdataId: currentSalesAssociate?.store,
          },
        },
        optimisticResponse: {
          createSalesAssociateTrackingMovement: {
            success: true,
          },
        },
        onError: handleEventSendError('createSalesAssociateTrackingMovement'),
      })
    },
    [currentSalesAssociate]
  )

  const moveWaitingListItemToInServiceList = useCallback(
    (
      waitingListItem: ListsItem['waiting'],
      selectedMotivesOccurrences?: Occurrence[]
    ) => {
      moveItemFromWaitingListToInService({
        ...waitingListItem,
        kind: List.InService,
        displaydServiceInfo: getDisplaydServiceInfo(selectedMotivesOccurrences),
        service: {
          startDate: new Date(),
          motives: selectedMotivesOccurrences,
        },
      })

      logger.info({
        message:
          'Moved waiting for service sales associate to in service (started a service)',
        workflowType: 'move-sales-associate',
        flow: 'moveWaitingListItemToInServiceList',
        context: 'turnlist-page',
        step: 'start-service',
        data: { salesAssociateId: waitingListItem.salesAssociate.id },
      })
    },
    [moveItemFromWaitingListToInService]
  )

  const moveInServiceListItemToWaitingList = useCallback(
    (
      inServiceListItem: ListsItem['inService'],
      selectedOccurrences: Occurrence[] = []
    ) => {
      const positioningRule = getLastPositioningRule(selectedOccurrences)

      const positionReferenceTime = getPositionReferenceTimeByPositioningRule(
        positioningRule,
        inServiceListItem.positionReferenceTime!,
        positionsLists.waitingList
      )

      moveItemFromInServiceListToWaitingList({
        ...inServiceListItem,
        kind: List.Waiting,
        service: undefined,
        displaydServiceInfo: getDisplaydServiceInfo(selectedOccurrences),
        positionReferenceTime,
      })

      if (!inServiceListItem.service) return

      logger.info({
        message:
          'Moved in service sales associate to waiting for service list (finished a service)',
        workflowType: 'move-sales-associate',
        flow: 'moveInServiceListItemToWaitingList',
        context: 'turnlist-page',
        step: 'end-service',
        data: { salesAssociateId: inServiceListItem.salesAssociate.id },
      })

      commitMutation<TurnListProvider_sendServiceEvent_Mutation>(environment, {
        mutation: graphql`
          mutation TurnListProvider_sendServiceEvent_Mutation(
            $input: CreateServiceInput!
          ) @raw_response_type {
            createSalesAssociateTrackingService(input: $input) {
              success
            }
          }
        `,
        variables: {
          input: {
            salesAssociateId: inServiceListItem.salesAssociate.id,
            startDate: inServiceListItem.service.startDate,
            endDate: new Date(),
            ...getServiceResultsAndRelations(
              selectedOccurrences,
              inServiceListItem.salesAssociate.id
            ),
            storeMasterdataId: currentSalesAssociate?.store,
          },
        },
        optimisticResponse: {
          createSalesAssociateTrackingService: {
            success: true,
          },
        },
        onError: handleEventSendError('createSalesAssociateTrackingService'),
      })
    },
    [
      moveItemFromInServiceListToWaitingList,
      positionsLists.waitingList,
      currentSalesAssociate,
    ]
  )

  const moveOutOfStoreItemToWaitingList = useCallback(
    (outOfSellerItem: ListsItem['outOfStore']) => {
      const intervalMovement = outOfSellerItem.unfinishedMovements.find(
        (movement) => movement.id !== movementFlow.id
      )

      const positionReferenceTime = outOfSellerItem.positionReferenceTime
        ? getPositionReferenceTimeByPositioningRule(
            intervalMovement?.positioningRule ?? movementFlow.positioningRule,
            outOfSellerItem.positionReferenceTime,
            positionsLists.waitingList
          )
        : new Date()

      const item: ListsItem['waiting'] = {
        kind: List.Waiting,
        salesAssociate: outOfSellerItem.salesAssociate,
        unfinishedMovements: [movementFlow],
        positionReferenceTime,
      }

      moveItemFromOutOfStoreToWaiting(item)

      logger.info({
        message:
          'Moved out of store sales associate to waiting for service list (entered the store)',
        workflowType: 'move-sales-associate',
        flow: 'moveOutOfStoreItemToWaitingList',
        context: 'turnlist-page',
        data: { salesAssociateId: outOfSellerItem.salesAssociate.id },
      })

      if (intervalMovement) {
        sendMovementEvent({
          salesAssociateId: outOfSellerItem.salesAssociate.id,
          movementId: intervalMovement.stepToClose?.id,
        })
      } else {
        sendMovementEvent({
          salesAssociateId: outOfSellerItem.salesAssociate.id,
          movementId: movementFlow?.id,
        })
      }
    },
    [
      moveItemFromOutOfStoreToWaiting,
      movementFlow,
      positionsLists.waitingList,
      sendMovementEvent,
    ]
  )

  const moveWaitingListItemToOutOfStore = useCallback(
    (
      waitingListItem: ListsItem['waiting'],
      selectedMovement: TurnListProvider_movement$data
    ) => {
      const unfinishedMovements = getUnfinishedMovementsBySelectedMovement(
        waitingListItem.unfinishedMovements,
        selectedMovement
      )

      moveItemFromWaitingListToOutOfStore({
        kind: List.OutOfStore,
        salesAssociate: waitingListItem.salesAssociate,
        positionReferenceTime: waitingListItem.positionReferenceTime,
        lastMovement: selectedMovement,
        unfinishedMovements,
      })

      logger.info({
        message:
          'Moved waiting for service sales associate to out of store (exited from store)',
        workflowType: 'move-sales-associate',
        flow: 'moveWaitingListItemToOutOfStore',
        context: 'turnlist-page',
        data: { salesAssociateId: waitingListItem.salesAssociate.id },
      })

      sendMovementEvent({
        salesAssociateId: waitingListItem.salesAssociate.id,
        movementId: selectedMovement?.id,
      })
    },
    [moveItemFromWaitingListToOutOfStore, sendMovementEvent]
  )

  return (
    <TurnListContext.Provider
      value={{
        movementFlow,
        occurrenceTrees,
        moveWaitingListItemToInServiceList,
        moveInServiceListItemToWaitingList,
        moveOutOfStoreItemToWaitingList,
        moveWaitingListItemToOutOfStore,
        salesAssociates,
      }}
    >
      {children}
    </TurnListContext.Provider>
  )
}
